// zod schemas per component. Keep in sync with the table in CLAUDE.md.
import { z } from "zod";
export const COMPONENT_NAMES = [
    "callout",
    "checklist",
    "table",
    "mermaid",
    "decision",
    "timeline",
    "cards",
    "stats",
    "chart",
    "columns",
];
export const TEXT_COMPONENTS = ["callout", "checklist", "table", "mermaid"];
export function isComponentName(name) {
    return COMPONENT_NAMES.includes(name);
}
/** Text components take their content in the fence body; structured ones take YAML. */
export function isTextComponent(name) {
    return TEXT_COMPONENTS.includes(name);
}
/** Prefix used when assigning ids (`<prefix><n>`). Headings use `h`. */
export const ID_PREFIX = {
    heading: "h",
    callout: "c",
    checklist: "k",
    table: "tb",
    mermaid: "m",
    decision: "d",
    timeline: "tl",
    cards: "cd",
    stats: "s",
    chart: "ch",
    columns: "col",
};
export const TONES = ["info", "tip", "warning", "danger", "note"];
export const toneSchema = z.enum(TONES);
/**
 * Stats read as good / bad / neutral rather than info / warning. Models also guess
 * `success`, `positive` and `negative`; accept those as aliases, and keep the callout
 * tones working for notes written before this existed.
 */
export const STAT_TONES = ["good", "bad", "neutral", ...TONES];
const STAT_TONE_ALIASES = {
    success: "good",
    positive: "good",
    up: "good",
    negative: "bad",
    failure: "bad",
    down: "bad",
    flat: "neutral",
};
export const statToneSchema = z.preprocess((v) => (typeof v === "string" && v in STAT_TONE_ALIASES ? STAT_TONE_ALIASES[v] : v), z.enum(STAT_TONES));
const str = z.string();
const nonEmpty = z.string().min(1, "must not be empty");
/** YAML turns unquoted numbers/dates into non-strings; accept scalars and stringify them. */
const scalarString = z.union([z.string(), z.number(), z.boolean()]).transform((v) => String(v));
export const calloutSchema = z
    .object({
    tone: toneSchema.default("info"),
    title: str.optional(),
})
    .strict();
export const checklistSchema = z
    .object({
    items: z.array(z.object({ text: str, checked: z.boolean() }).strict()).min(1, "needs at least one task item"),
})
    .strict();
export const tableSchema = z
    .object({
    header: z.array(str).min(1),
    rows: z.array(z.array(str)),
})
    .strict();
export const mermaidSchema = z.object({}).strict();
export const decisionSchema = z
    .object({
    question: nonEmpty,
    options: z
        .array(z.object({ id: scalarString.optional(), label: scalarString, note: scalarString.optional() }).strict())
        .min(1, "needs at least one option"),
    chosen: scalarString.optional(),
    rationale: str.optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    const ids = v.options.map((o) => o.id).filter((x) => x !== undefined);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "option ids must be unique" });
    }
    if (v.chosen !== undefined) {
        const hit = v.options.some((o) => o.id === v.chosen) || v.options.some((o) => o.label === v.chosen);
        if (!hit) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["chosen"],
                message: `"${v.chosen}" matches no option id or label (options: ${v.options
                    .map((o) => o.id ?? o.label)
                    .join(", ")})`,
            });
        }
    }
});
export const timelineSchema = z
    .object({
    items: z
        .array(z
        .object({
        date: scalarString.optional(),
        title: scalarString,
        body: str.optional(),
        status: z.enum(["done", "current", "upcoming"]).optional(),
    })
        .strict())
        .min(1, "needs at least one item"),
})
    .strict();
export const cardsSchema = z
    .object({
    columns: z.union([z.literal(2), z.literal(3)]).optional(),
    items: z
        .array(z.object({ title: scalarString, body: str.optional(), tone: toneSchema.optional() }).strict())
        .min(1, "needs at least one item"),
})
    .strict();
export const statsSchema = z
    .object({
    items: z
        .array(z
        .object({
        label: scalarString,
        value: scalarString,
        delta: scalarString.optional(),
        tone: statToneSchema.optional(),
    })
        .strict())
        .min(1, "needs at least one item"),
})
    .strict();
export const chartSchema = z
    .object({
    type: z.enum(["bar", "line", "area", "pie"]),
    x: z.array(scalarString).min(1),
    series: z
        .array(z.object({ name: scalarString, data: z.array(z.number()) }).strict())
        .min(1, "needs at least one series"),
    title: str.optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    v.series.forEach((s, i) => {
        if (s.data.length !== v.x.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["series", i, "data"],
                message: `series "${s.name}" has ${s.data.length} values but x has ${v.x.length}`,
            });
        }
    });
});
export const columnsSchema = z
    .object({
    count: z.union([z.literal(2), z.literal(3)]).optional(),
})
    .strict();
export const COMPONENT_SCHEMAS = {
    callout: calloutSchema,
    checklist: checklistSchema,
    table: tableSchema,
    mermaid: mermaidSchema,
    decision: decisionSchema,
    timeline: timelineSchema,
    cards: cardsSchema,
    stats: statsSchema,
    chart: chartSchema,
    columns: columnsSchema,
};
function pathOf(issue) {
    return issue.path.length ? issue.path.map((p) => (typeof p === "number" ? `[${p}]` : p)).join(".").replace(/\.\[/g, "[") : "";
}
function describeIssue(issue) {
    const path = pathOf(issue);
    const at = path ? `${path}: ` : "";
    switch (issue.code) {
        case "invalid_enum_value":
            return `${at}must be one of ${issue.options.map(String).join("|")} (got ${JSON.stringify(issue.received)})`;
        case "invalid_type":
            if (issue.received === "undefined")
                return `${path || "value"} is required`;
            return `${at}expected ${issue.expected}, got ${issue.received}`;
        case "unrecognized_keys":
            return `${at}unknown key${issue.keys.length > 1 ? "s" : ""} ${issue.keys.map((k) => `"${k}"`).join(", ")}`;
        case "too_small":
            if (issue.type === "array")
                return `${at}needs at least ${issue.minimum} item${issue.minimum === 1 ? "" : "s"}`;
            if (issue.type === "string")
                return `${at}must not be empty`;
            return `${at}${issue.message}`;
        case "invalid_literal":
            return `${at}must be ${JSON.stringify(issue.expected)}`;
        case "invalid_union": {
            const firsts = issue.unionErrors.map((e) => e.issues[0]).filter((x) => !!x);
            if (firsts.length && firsts.every((f) => f.code === "invalid_type" && f.received === "undefined")) {
                return `${path || "value"} is required`;
            }
            const expected = [...new Set(firsts.map((f) => (f.code === "invalid_type" ? f.expected : f.code === "invalid_literal" ? JSON.stringify(f.expected) : "")).filter(Boolean))];
            return `${at}expected ${expected.join("|") || "a valid value"}`;
        }
        default:
            return `${at}${issue.message}`;
    }
}
/** Validate props for a component. Error messages list the failing paths, one per line. */
export function validateComponent(name, props) {
    if (!isComponentName(name)) {
        return { ok: false, error: `unknown component "${name}" (known: ${COMPONENT_NAMES.join(", ")})` };
    }
    const result = COMPONENT_SCHEMAS[name].safeParse(props);
    if (result.success)
        return { ok: true, props: result.data };
    const seen = new Set();
    const lines = [];
    for (const issue of result.error.issues) {
        const line = describeIssue(issue);
        if (!seen.has(line)) {
            seen.add(line);
            lines.push(line);
        }
    }
    return { ok: false, error: `${name}: ${lines.join("; ")}` };
}
//# sourceMappingURL=schemas.js.map